/**
 * Service: Platform Config, Pricing Plans, Credit Packs
 * Wraps all platform-level Supabase operations.
 */
import { supabase } from "@/integrations/supabase/client";

/* ══════════════════════════════════════
   Platform Config
   ══════════════════════════════════════ */

export async function getPlatformConfig(key: string) {
  return supabase
    .from("platform_config" as any)
    .select("value")
    .eq("key", key)
    .single();
}

export async function setPlatformConfig(key: string, value: any) {
  const { error } = await supabase
    .from("platform_config" as any)
    .update({ value } as any)
    .eq("key", key);

  if (error) {
    // Row may not exist — upsert
    return supabase
      .from("platform_config" as any)
      .upsert({ key, value } as any);
  }
  return { error: null };
}

/* ══════════════════════════════════════
   Pricing Plans
   ══════════════════════════════════════ */

export interface PricingPlanRow {
  id: string;
  name: string;
  price_label: string;
  period: string;
  description: string;
  features: string[];
  cta_label: string;
  is_highlighted: boolean;
  is_active: boolean;
  sort_order: number;
  setup_fee_cents: number;
  monthly_fee_cents: number;
  per_card_fee_cents: number;
  monthly_generation_pool: number;
  cards_included_in_monthly: number;
}

export async function listPricingPlans() {
  return supabase
    .from("pricing_plans" as any)
    .select("*")
    .order("sort_order");
}

export async function listActivePricingPlans() {
  return supabase
    .from("pricing_plans" as any)
    .select("id, name, monthly_generation_pool")
    .eq("is_active", true)
    .order("sort_order");
}

export async function replaceAllPricingPlans(rows: Omit<PricingPlanRow, "id">[]) {
  // Delete all, then insert
  await supabase
    .from("pricing_plans" as any)
    .delete()
    .neq("id", "00000000-0000-0000-0000-000000000000");
  return supabase.from("pricing_plans" as any).insert(rows as any);
}

/* ══════════════════════════════════════
   Credit Packs
   ══════════════════════════════════════ */

export interface CreditPackRow {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  currency: string;
  is_active: boolean;
  sort_order: number;
}

export async function listCreditPacks() {
  return supabase.from("credit_packs").select("*").order("sort_order");
}

export async function listCreditPackIds() {
  return supabase.from("credit_packs").select("id");
}

export async function insertCreditPack(payload: Omit<CreditPackRow, "id">) {
  return supabase.from("credit_packs").insert(payload);
}

export async function updateCreditPack(id: string, payload: Partial<CreditPackRow>) {
  return supabase.from("credit_packs").update(payload).eq("id", id);
}

export async function deleteCreditPack(id: string) {
  return supabase.from("credit_packs").delete().eq("id", id);
}

/* ══════════════════════════════════════
   Policy Templates
   ══════════════════════════════════════ */

export async function listPolicyTemplates() {
  return supabase
    .from("policy_templates")
    .select("*")
    .order("template_key");
}

export async function getPolicyTemplateByKey(templateKey: string) {
  return supabase
    .from("policy_templates")
    .select("template_html")
    .eq("template_key", templateKey)
    .single();
}

export async function updatePolicyTemplate(id: string, templateHtml: string) {
  return supabase
    .from("policy_templates")
    .update({ template_html: templateHtml })
    .eq("id", id);
}

/* ══════════════════════════════════════
   Pricing Plan Options (for selects)
   ══════════════════════════════════════ */

export async function listActivePricingPlanOptions() {
  return supabase
    .from("pricing_plans" as any)
    .select("id, name")
    .eq("is_active", true)
    .order("sort_order");
}
